import {
  ModalDeleteGroup,
  SettingsModal,
} from "features/ProductsGroup/components";
import { ProductProperties } from "features/ProductProperties/ProductProperties";
import { atom } from "jotai";
import { ReactElement } from "react";
import { EmployeesGroupModal } from "features/EmployeesGroupModal/EmployeesGroupModal";
import { ClientGroupInfo } from "features/ClientGroupInfo/ClientGroupInfo";
import { ClientInfo } from "features/ClientInfo/ClientInfo";
import { DocumentModal } from "features/DocumentModal/DocumentModal";
import { AssignEmployeeModal } from "features/Employees/components";
import { AddEditEmployee } from "features/Employees/components";
import {
  ChangeGroupModal,
  AccureBonusesModal,
} from "features/Clients/components";
import { AddPrintTemplate } from "features/PrintTemplates/components";

import {
  ConfirmationModal,
  ConfirmationModalProps,
} from "components/ConfirmationModal/ConfirmationModal";
import { RadioButtons } from "components/RadioButtons/RadioButtons";
import { InfoModal, InfoModalProps } from "components/InfoModal/InfoModal";
import {
  SelectModal,
  SelectModalProps,
} from "components/SelectModal/SelectModal";
import {
  LoadingModal,
  LoadingModalProps,
} from "components/LoadingModal/InfoModal";
import {
  ItemGroupsSelectModal,
  ItemGroupsSelectModalProps,
} from "components/ItemGroupsSelectModal/ItemGroupsSelectModal";

type ModalAtomState = {
  isOpen: boolean;
  children?: ReactElement;
};

export const setModalAtom = atom(null, (_, set, update: ModalAtomState) => {
  set(modalAtom, update);
});

export const setDeleteModalGroupAtom = atom(null, (_, set) => {
  const update: ModalAtomState = {
    isOpen: true,
    children: <ModalDeleteGroup />,
  };

  set(modalAtom, update);
});

export const setSettingsModalGroupAtom = atom(
  null,
  (_, set, isEdit: boolean) => {
    const update: ModalAtomState = {
      isOpen: true,
      children: <SettingsModal isEdit={isEdit} />,
    };

    set(modalAtom, update);
  }
);

export const setModalCloseAtom = atom(null, (_, set) => {
  set(modalAtom, { isOpen: false });
});

export const setModal2CloseAtom = atom(null, (_, set) => {
  set(modal2Atom, { isOpen: false });
});

export const modalAtom = atom<ModalAtomState>({
  isOpen: false,
});

export const modal2Atom = atom<ModalAtomState>({
  isOpen: false,
});

export const setPropertiesProductAtom = atom(
  null,
  (_, set, isEdit: boolean) => {
    const update: ModalAtomState = {
      isOpen: true,
      children: <ProductProperties isEdit={isEdit} />,
    };

    set(modalAtom, update);
  }
);

export const setEmployeesGroupModalAtom = atom(
  null,
  (_, set, isEdit?: boolean) => {
    const update: ModalAtomState = {
      isOpen: true,
      children: <EmployeesGroupModal isEdit={!!isEdit} />,
    };

    set(modalAtom, update);
  }
);

export const setClientGroupAtom = atom(null, (_, set, isEdit?: boolean) => {
  const update: ModalAtomState = {
    isOpen: true,
    children: <ClientGroupInfo isEdit={isEdit} />,
  };

  set(modalAtom, update);
});

export const setClientModalAtom = atom(null, (_, set, isEdit?: boolean) => {
  const update: ModalAtomState = {
    isOpen: true,
    children: <ClientInfo isEdit={isEdit} />,
  };

  set(modalAtom, update);
});

export const setDocumentModalAtom = atom(null, (_, set) => {
  const update: ModalAtomState = {
    isOpen: true,
    children: <DocumentModal />,
  };

  set(modalAtom, update);
});

export const setConfirmationModalAtom = atom(
  null,
  (
    _,
    set,
    {
      title,
      onConfirm,
      confirmText,
      atomToOpen = modalAtom,
      setAtomClose = setModalCloseAtom,
      height,
      width,
    }: ConfirmationModalProps
  ) => {
    const update: ModalAtomState = {
      isOpen: true,
      children: (
        <ConfirmationModal
          title={title}
          onConfirm={onConfirm}
          confirmText={confirmText}
          setAtomClose={setAtomClose}
          height={height}
          width={width}
        />
      ),
    };

    set(atomToOpen, update);
  }
);

export const setInfoModalAtom = atom(
  null,
  (
    _,
    set,
    {
      title,
      onOkClick,
      buttonText,
      atomToOpen = modalAtom,
      setAtomClose = setModalCloseAtom,
      height,
    }: InfoModalProps
  ) => {
    const update: ModalAtomState = {
      isOpen: true,
      children: (
        <InfoModal
          title={title}
          onOkClick={onOkClick}
          buttonText={buttonText}
          setAtomClose={setAtomClose}
          height={height}
        />
      ),
    };

    set(atomToOpen, update);
  }
);

export const setLoadingModalAtom = atom(
  null,
  (_, set, { atomToOpen = modalAtom, ...props }: LoadingModalProps) => {
    const update: ModalAtomState = {
      isOpen: true,
      children: <LoadingModal {...props} />,
    };

    set(atomToOpen, update);
  }
);

export const setSelectModalAtom = atom(
  null,
  (
    _,
    set,
    {
      title,
      onSelectClick,
      buttonText,
      atomToOpen = modalAtom,
      setAtomClose = setModalCloseAtom,
      options,
    }: SelectModalProps
  ) => {
    const update: ModalAtomState = {
      isOpen: true,
      children: (
        <SelectModal
          title={title}
          buttonText={buttonText}
          setAtomClose={setAtomClose}
          onSelectClick={onSelectClick}
          options={options}
        />
      ),
    };

    set(atomToOpen, update);
  }
);

export const openItemGroupsSelectModalAtom = atom(null, (_, set) => {
  const update: ModalAtomState = {
    isOpen: true,
    children: <ItemGroupsSelectModal />,
  };

  set(modal2Atom, update);
});

export const setRadioButtonsModalAtom = atom(
  null,
  (_, set, title: string, isEditGroup?: boolean) => {
    const update: ModalAtomState = {
      isOpen: true,
      children: <RadioButtons title={title} isEditGroup={isEditGroup} />,
    };

    set(modalAtom, update);
  }
);

export const setAssignEmployeeModalAtom = atom(null, (_, set) => {
  const update: ModalAtomState = {
    isOpen: true,
    children: <AssignEmployeeModal />,
  };

  set(modalAtom, update);
});

export const setChangeClientGroupModalAtom = atom(null, (_, set) => {
  const update: ModalAtomState = {
    isOpen: true,
    children: <ChangeGroupModal />,
  };

  set(modalAtom, update);
});

export const setAccureBonusesModalAtom = atom(null, (_, set) => {
  const update: ModalAtomState = {
    isOpen: true,
    children: <AccureBonusesModal />,
  };

  set(modalAtom, update);
});

export const setAddEditEmployeeAtom = atom(
  null,
  (_, set, { isEdit }: { isEdit?: boolean }) => {
    const update: ModalAtomState = {
      isOpen: true,
      children: <AddEditEmployee isEdit={isEdit} />,
    };

    set(modalAtom, update);
  }
);

export const setAddPrintTemplateAtom = atom(
  null,
  (_, set, isEdit?: boolean) => {
    const update: ModalAtomState = {
      isOpen: true,
      children: <AddPrintTemplate isEdit={isEdit} />,
    };

    set(modalAtom, update);
  }
);
